import { Check, X, AlertTriangle, Archive } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { GoNoGoAssessment, GngDecision } from "@/stores/strategy-store"
import { cn } from "@/lib/utils"

interface GoNoGoDecisionCardProps {
  assessment: GoNoGoAssessment
  /** Omit to hide Reopen (a reader who can't edit the bid). */
  onReopen?: (() => void) | undefined
  onArchiveBid?: () => void
}

const decisionConfig: Record<GngDecision, {
  label: string
  icon: typeof Check
  borderClass: string
  iconBg: string
  iconColor: string
}> = {
  go: {
    label: 'Go',
    icon: Check,
    borderClass: 'bg-[hsl(var(--status-success)/0.04)] border-l-2 border-l-[hsl(var(--status-success))]',
    iconBg: 'bg-[hsl(var(--status-success)/0.1)]',
    iconColor: 'text-[hsl(var(--status-success))]',
  },
  conditional_go: {
    label: 'Conditional go',
    icon: AlertTriangle,
    borderClass: 'bg-[hsl(var(--status-warning)/0.04)] border-l-2 border-l-[hsl(var(--status-warning))]',
    iconBg: 'bg-[hsl(var(--status-warning)/0.1)]',
    iconColor: 'text-[hsl(var(--status-warning))]',
  },
  no_go: {
    label: 'No go',
    icon: X,
    borderClass: 'bg-[hsl(var(--destructive)/0.03)] border-l-2 border-l-[hsl(var(--destructive))]',
    iconBg: 'bg-[hsl(var(--destructive)/0.1)]',
    iconColor: 'text-[hsl(var(--destructive))]',
  },
}

export function GoNoGoDecisionCard({ assessment, onReopen, onArchiveBid }: GoNoGoDecisionCardProps) {
  if (!assessment.decision) return null

  const config = decisionConfig[assessment.decision]
  const Icon = config.icon
  const decidedDate = assessment.decidedAt ? new Date(assessment.decidedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''

  return (
    <div className="space-y-4">
      <div
        data-testid="decision-card"
        data-decision={assessment.decision}
        className={cn("border border-border rounded-xl bg-card shadow-sm p-5", config.borderClass)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", config.iconBg)}>
              <Icon className={cn("h-5 w-5", config.iconColor)} />
            </div>
            <div>
              <h2 className="text-lg font-semibold leading-tight">{config.label}</h2>
              <div className="text-meta mt-0.5">
                Score {assessment.overallScore ?? 0} / 100
                {decidedDate && <> &middot; decided {decidedDate}</>}
              </div>
            </div>
          </div>
          {onReopen && (
            <Button variant="ghost" size="sm" className="text-meta" onClick={onReopen}>
              Reopen
            </Button>
          )}
        </div>

        {(assessment.decisionNotes || assessment.decisionConditions) && (
          <div className="mt-3 space-y-1">
            {assessment.decisionConditions && (
              <p className="text-sm text-foreground">
                <span className="font-medium">Conditions:</span> {assessment.decisionConditions}
              </p>
            )}
            {assessment.decisionNotes && (
              <p className="text-sm text-muted-foreground italic">
                &ldquo;{assessment.decisionNotes}&rdquo;
              </p>
            )}
          </div>
        )}
      </div>

      {/* Archive prompt for No-Go */}
      {assessment.decision === 'no_go' && onArchiveBid && (
        <div className="bg-muted/50 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Archive className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">This bid was marked as No go</div>
              <div className="text-meta mt-0.5">
                Archive it to hide from your active bids list.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm">Keep Open</Button>
            <Button size="sm" onClick={onArchiveBid}>Archive Bid</Button>
          </div>
        </div>
      )}
    </div>
  )
}
